/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useRef,
} from 'react'
import {
  Breakpoint,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  DialogActionsProps,
  DialogTitleProps,
  DialogContentProps,
  ButtonProps,
} from '@mui/material'
import { AppDialogCallToAction } from './appDialogContext'
import { useLocation } from 'react-router-dom'

const DEFAULT_CALL_TO_ACTION: AppDialogCallToAction = {
  text: 'Save',
  proceedHandler: () => undefined,
  colour: 'info',
}

export type BreakpointOptions = Breakpoint | false | undefined

/**
 * @property dialogActions should be wrapped by <DialgoActions> react node
 */
export interface AppDialogOpenOptions {
  maxWidth?: BreakpointOptions
  title?: ReactNode
  content: ReactNode
  actions?: AppDialogActions | AppDialogActions[] | null
  dialogActionsProps?: DialogActionsProps
  dialogTitleProps?: DialogTitleProps
  dialogContentProps?: DialogContentProps
  removeDismiss?: boolean
  initialData?: { [key: string]: unknown }
  onClose?: () => void
}

export interface AppDialogActions {
  dismissText?: string
  callToAction?: AppDialogCallToAction
  buttonProps?: {
    closeOnClick?: boolean
    actionMethod?: (...args: any) => void
  } & ButtonProps
}

export interface AppDialogRef {
  open: ({
    maxWidth,
    content,
    title,
    actions,
    dialogActionsProps,
    dialogTitleProps,
    dialogContentProps,
  }: AppDialogOpenOptions) => void
  isDisabled: boolean
  setIsDisabled: (newValue: boolean) => void
  callToAction: AppDialogCallToAction
  handleClose: () => void
  setCallToAction: (newValue: AppDialogCallToAction) => void
  setData: (data: unknown) => void
}

/**
 * The dialog UI that can be opened from anywhere in the app, with the title
 * and content provided by the caller.
 *
 * Note: isDisabled and callToAction can be manipulated anywhere and at any time. So it is important that only
 * one dialog is in use at a time so other calls to setIsDisabled/setCallToAction only effect the open dialog
 */
const AppDialog = forwardRef((_, dialogRef) => {
  const location = useLocation()
  const [maxWidth, setMaxWidth] = useState<BreakpointOptions>()
  const [title, setTitle] = useState<ReactNode>()
  const [content, setContent] = useState<ReactNode>()
  const [dialogActionsProps, setDialogActionsProps] =
    useState<DialogActionsProps>()
  const [dialogTitleProps, setDialogTitleProps] = useState<DialogTitleProps>()
  const [dialogContentProps, setDialogContentProps] =
    useState<DialogContentProps>()
  const [actions, setActions] = useState<
    AppDialogActions | AppDialogActions[] | undefined | null
  >(null)
  const [removeDismiss, setRemoveDismiss] = useState<boolean>(false)

  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [callToAction, setCallToAction] = useState<AppDialogCallToAction>(
    DEFAULT_CALL_TO_ACTION
  )
  const [data, setData] = useState<{ [key: string]: unknown } | undefined>()
  const [userOnClose, setUserOnClose] = useState<(() => void) | undefined>(
    undefined
  )
  const userOnCloseRef = useRef<(() => void) | undefined>(undefined)

  const handleClose = useCallback(() => {
    if (Array.isArray(actions)) {
      const closeActionMethod = actions.find(
        (action) => action.buttonProps?.key === 'close'
      )?.buttonProps

      if (closeActionMethod?.actionMethod) {
        if (data?.['close']) {
          closeActionMethod.actionMethod(data['close'])
        } else {
          closeActionMethod.actionMethod()
        }
      }
    }

    userOnCloseRef.current && userOnCloseRef.current()

    setContent(null)
    setIsDisabled(false)
  }, [actions, data])

  // We enhance the supplied ref here with a method to open the dialog in
  // a way that is scoped to this component.
  //
  // The intention is that the ref is supplied by the AppDialogProvider
  // which renders this component as well as the rest of the app. When
  // the useAppDialog() hook is called within the rest of the app we
  // don't change anything in the AppDialogProvider which would otherwise
  // trigger a re-render of the entire app (a performance killer).
  useImperativeHandle(dialogRef, () => ({
    open: ({
      maxWidth = 'md',
      title = '',
      content,
      actions,
      dialogActionsProps,
      dialogTitleProps,
      dialogContentProps,
      removeDismiss = false,
      initialData,
      onClose,
    }: AppDialogOpenOptions) => {
      // Reset state before opening a new dialog
      setContent(null)
      setActions(null)
      setData(undefined)
      setIsDisabled(false)
      setMaxWidth(maxWidth)
      setTitle(title)
      setContent(content)
      setActions(actions)
      setDialogActionsProps(dialogActionsProps)
      setDialogTitleProps(dialogTitleProps)
      setDialogContentProps(dialogContentProps)
      setRemoveDismiss(removeDismiss)
      setData(initialData)

      //This looks insane, but React will call the first function in the setter,
      //and the second when we actually close the dialog
      setUserOnClose(() => () => {
        onClose?.()
      })
      userOnCloseRef.current = onClose
    },
    isDisabled,
    setIsDisabled,
    handleClose,
    callToAction,
    setCallToAction,
    data,
    setData,
  }))

  /**
   * Closes dialog when the URL/route changes
   */
  useEffect(() => {
    handleClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  /**
   * Updates the dialog's actions, callToAction button when it is set via the hook
   */
  useEffect(() => {
    setActions((prevActions) => ({
      ...prevActions,
      callToAction: callToAction,
    }))
  }, [callToAction])

  const onClickHandler = (
    key: string,
    closeOnClick: boolean,
    actionMethod: ((...args: any) => void) | undefined
  ) => {
    if (key === 'close') {
      return handleClose()
    } else if (closeOnClick && actionMethod) {
      if (data) {
        return handleProceed(() => actionMethod(data[key]))
      } else {
        return handleProceed(() => actionMethod())
      }
    }

    return actionMethod && actionMethod(data?.[key])
  }

  return (
    <Dialog
      open={content !== null}
      onClose={() => {
        userOnClose?.()
        handleClose?.()
      }}
      fullWidth={true}
      maxWidth={maxWidth}
      scroll="paper"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
      PaperProps={{
        sx: {
          mt: 8,
        },
      }}
    >
      {title && <DialogTitle {...dialogTitleProps}>{title}</DialogTitle>}

      <DialogContent {...dialogContentProps}>{content}</DialogContent>

      {actions && (
        <DialogActions {...dialogActionsProps}>
          {!Array.isArray(actions) ? (
            <>
              {!removeDismiss && (
                <Button
                  // If actions.callToAction is not set, then it means that the dismiss button is
                  // the only button in the dialog, so we use 'contained' variant
                  variant={actions.callToAction ? 'outlined' : 'contained'}
                  onClick={handleClose}
                  sx={{
                    width: '10rem',
                  }}
                >
                  {actions.dismissText ?? 'Cancel'}
                </Button>
              )}
              {actions.callToAction && (
                <Button
                  variant="contained"
                  onClick={() =>
                    actions.callToAction
                      ? handleProceed(actions.callToAction.proceedHandler)
                      : () => undefined
                  }
                  color={actions.callToAction.colour ?? 'info'}
                  disabled={isDisabled}
                  sx={{
                    width: actions.callToAction.width ?? '10rem',
                  }}
                >
                  {actions.callToAction.text}
                </Button>
              )}
            </>
          ) : (
            actions.map((action: AppDialogActions) => {
              if (!action.buttonProps) return <></>

              const { closeOnClick, actionMethod, key, ...buttonProps } =
                action.buttonProps

              return (
                key != null && (
                  <Button
                    key={key}
                    variant="outlined"
                    onClick={() =>
                      onClickHandler(
                        key as string,
                        closeOnClick as boolean,
                        actionMethod
                      )
                    }
                    {...buttonProps}
                  >
                    {buttonProps.title ?? 'Cancel'}
                  </Button>
                )
              )
            })
          )}
        </DialogActions>
      )}
    </Dialog>
  )

  /**
   * Handles closing the dialog after the call to action button has been pressed
   * @param proceedHandler
   */
  function handleProceed(proceedHandler: () => void | Promise<void>) {
    proceedHandler()
    handleClose()
  }
})
AppDialog.displayName = 'AppDialog'

export default AppDialog
